import { Fragment } from "react";
import {
  cardRows,
  contestTeams,
  MARKET_KINDS,
  type Board,
  type CardRow,
  type LineOverrides,
  type MarketKind,
} from "../lib/board";
import { cn } from "../lib/cn";
import { fmtStart, sideLineText } from "../lib/format";
import type { Contest, Partner } from "../lib/types";
import { Mark } from "./Mark";
import { PriceCell } from "./PriceCell";

/**
 * Shared by the cards and the column headings so they line up at every width. Phone: team +
 * three narrow price columns, with the start time above the rows. Wider: a start-time column on
 * the left.
 */
export const BOARD_GRID =
  "grid grid-cols-[minmax(0,1fr)_repeat(3,minmax(0,4.75rem))] items-center gap-x-1.5 gap-y-1.5 " +
  "sm:grid-cols-[5.25rem_minmax(0,1fr)_repeat(3,minmax(0,13rem))] sm:gap-x-2.5";

export function GameCard({
  contest,
  board,
  league,
  overrides,
  selectedKind,
  onSelect,
  onOpen,
  partners,
  index,
}: {
  contest: Contest;
  board: Board;
  league?: string;
  /** Lines the viewer flipped to on this game. */
  overrides?: LineOverrides;
  selectedKind: MarketKind | null;
  onSelect: (kind: MarketKind) => void;
  /** Open the full game: every market, props included. */
  onOpen: () => void;
  partners?: Map<string, Partner>;
  index: number;
}) {
  const rows = cardRows(board, contestTeams(contest), overrides);
  const more =
    board.extraMarkets > 0 ? (
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1 rounded-full border border-line bg-panel/60 px-2 py-0.5 text-[10px] font-semibold text-dim transition-colors hover:border-accent hover:text-ink"
      >
        +{board.extraMarkets} markets
      </button>
    ) : null;

  return (
    <article
      className={cn(
        "rise rounded-2xl border bg-card/80 px-2.5 py-2.5 backdrop-blur-sm transition-colors sm:px-3",
        selectedKind ? "border-accent/60 shadow-[0_0_32px_-14px_var(--color-accent)]" : "border-line hover:border-cell-hi",
      )}
      style={{ animationDelay: `${Math.min(index, 12) * 25}ms` }}
    >
      <div className="mb-2 flex items-center justify-between gap-2 sm:hidden">
        <When contest={contest} league={league} inline />
        {more}
      </div>
      <div className={BOARD_GRID}>
        <div
          className="hidden self-start pt-0.5 sm:flex sm:flex-col sm:items-start sm:gap-1.5"
          style={{ gridRow: `span ${rows.length} / span ${rows.length}` }}
        >
          <When contest={contest} league={league} />
          {more}
        </div>
        {rows.map((row) => (
          <Fragment key={row.key}>
            <Team row={row} onOpen={onOpen} />
            {MARKET_KINDS.map((kind) => {
              const side = row[kind];
              return (
                <PriceCell
                  key={kind}
                  side={side}
                  lineText={side ? sideLineText(side) : null}
                  selected={selectedKind === kind}
                  onSelect={() => onSelect(kind)}
                  partners={partners}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
    </article>
  );
}

/** A team name opens the game's own page. */
function Team({ row, onOpen }: { row: CardRow; onOpen: () => void }) {
  const isDraw = row.key === "draw" || row.key === "tie";
  return (
    <button
      type="button"
      onClick={onOpen}
      title="See this game"
      className="group flex min-w-0 items-center gap-2 self-stretch rounded-lg text-left"
    >
      {isDraw ? (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-line text-[10px] font-bold text-faint">
          X
        </span>
      ) : (
        <Mark name={row.label} src={row.imageUrl} size={20} round />
      )}
      <span
        className={cn(
          "truncate text-[14px] font-semibold decoration-accent underline-offset-4 group-hover:underline",
          isDraw && "font-medium text-dim",
        )}
      >
        {row.label}
      </span>
    </button>
  );
}

export function When({ contest, league, inline = false }: { contest: Contest; league?: string; inline?: boolean }) {
  const s = fmtStart(contest.starts_at);
  const time = s.live ? (
    <span className="inline-flex items-center gap-1 font-bold text-live">
      <span className="pulse h-1.5 w-1.5 rounded-full bg-live" />
      Live
    </span>
  ) : (
    <span className="font-bold text-ink">{s.time}</span>
  );

  if (inline) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-dim">
        {!s.live && s.day && <span>{s.day}</span>}
        {time}
        {league && (
          <>
            <span className="text-faint">·</span>
            <span className="font-semibold uppercase tracking-wide text-faint">{league}</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col text-[11px] leading-snug text-dim">
      {!s.live && s.day && <span>{s.day}</span>}
      <span className="text-[13px]">{time}</span>
      {league && <span className="mt-0.5 truncate font-semibold uppercase tracking-wide text-faint">{league}</span>}
    </div>
  );
}
